// คำนวณล้วน ๆ ของหน้า "รายงานบัญชีรายวัน" (ไม่มี fetch / ไม่มี React) — แยกไว้ให้เทสด้วยตัวเลขแผ่นบัญชีได้ตรง ๆ
// ทุกก้อนที่ "ควรเท่ากัน" ให้ส่วนต่างออกมาเป็น warnings — ห้ามปรับตัวเลขให้เท่ากันเอง

export const CASH_PREFIX = 'CASH-';
export const BANK_TRANSFER_CODE = 'BT';
export const FUEL_CODE = 'FUEL';
// "ขายกระสอบ" ในระบบ = เงินประกันกระสอบ (Vector 2026-10-07) — แถวส่วนที่ 1 ใช้ชื่อนี้ แยกตามราคา วางบนสุด
export const BAG_DEPOSIT_NAME = 'ประกันถุง';

// ชนิดเงินที่ยังนับแยกอยู่ (CASH_DENOMINATION) — ใช้เมื่อ API ตารางอ้างอิงล้ม เพื่อไม่ให้ตารางหาย
export const FALLBACK_DENOMS = [
    { value: 1000, name: '1,000 บาท' },
    { value: 500, name: '500 บาท' },
    { value: 100, name: '100 บาท' },
    { value: 50, name: '50 บาท' },
    { value: 20, name: '20 บาท' },
    { value: 1, name: 'เงินเหรียญ' },
];

const num = (v) => (typeof v === 'number' && !isNaN(v) ? v : (parseFloat(v) || 0));
// กันเศษทศนิยมลอยตัว (0.1+0.2) ก่อนเทียบ/แสดง
export const round2 = (v) => Math.round((num(v) + Number.EPSILON) * 100) / 100;

// ส่วนที่ 1 — รวมรายการขายตาม ชื่อสินค้า + ราคา/หน่วย เรียงราคาน้อย→มาก (ชื่อสินค้าเป็นตัวตัดสินเมื่อราคาเท่ากัน)
export const buildProductRows = (items, bagRows = []) => {
    const map = new Map();
    const add = (name, unitPrice, qty, total) => {
        const key = `${name}__${unitPrice}`;
        if (!map.has(key)) map.set(key, { name, unitPrice, qty: 0, total: 0 });
        const row = map.get(key);
        row.qty += qty;
        row.total += total;
    };
    items.forEach(it => {
        const unitPrice = num(it.unitPrice);
        const qty = num(it.quantity);
        add(it.productName || it.iceCode || '-', unitPrice, qty, it.total != null ? num(it.total) : qty * unitPrice);
    });
    bagRows.forEach(b => add(b.name, num(b.unitPrice), num(b.qty), num(b.total)));
    const isDeposit = (r) => r.name === BAG_DEPOSIT_NAME;
    const rows = Array.from(map.values()).sort((a, b) =>
        (isDeposit(b) - isDeposit(a)) || a.unitPrice - b.unitPrice || String(a.name).localeCompare(String(b.name)));
    return {
        rows,
        totalQty: rows.reduce((s, r) => s + r.qty, 0),
        totalAmount: round2(rows.reduce((s, r) => s + r.total, 0)),
    };
};

// ส่วนที่ 3/4 — จาก payments ของใบรายงานรอบ (payment_data): เงินสดแยกใบ, ยอดโอน, น้ำมันที่คนขับกรอก
// ชนิดที่ระบบเลิกนับแยกแล้ว (CASH-2/5/10 → หมวด CASH_COIN) พับลงช่องค่าเงินต่ำสุด = ช่องเหรียญ (ค่า 1 ⇒ จำนวน = บาท)
export const aggregatePayments = (payments, denoms) => {
    const denomList = (denoms && denoms.length ? denoms : FALLBACK_DENOMS).slice().sort((a, b) => a.value - b.value);
    const known = new Set(denomList.map(d => d.value));
    const foldValue = denomList[0].value;
    const byDenom = {};
    denomList.forEach(d => { byDenom[d.value] = { count: 0, total: 0 }; });
    let transfer = 0;
    let fuel = 0;
    (payments || []).forEach(p => {
        const code = p.paymentCode || '';
        const total = num(p.total);
        if (code === BANK_TRANSFER_CODE) transfer += total;
        else if (code === FUEL_CODE) fuel += total;
        else if (code.startsWith(CASH_PREFIX)) {
            const value = Number(code.slice(CASH_PREFIX.length)) || 0;
            const target = known.has(value) ? value : foldValue;
            byDenom[target].count += target === value ? num(p.amount) : total;
            byDenom[target].total += total;
        }
    });
    const cashTotal = Object.values(byDenom).reduce((s, x) => s + x.total, 0);
    return { denomList, byDenom, cashTotal: round2(cashTotal), transfer: round2(transfer), fuel: round2(fuel) };
};

export const mergeDenoms = (parts, denoms) => {
    const denomList = (denoms && denoms.length ? denoms : FALLBACK_DENOMS).slice().sort((a, b) => a.value - b.value);
    const byDenom = {};
    denomList.forEach(d => { byDenom[d.value] = { count: 0, total: 0 }; });
    let transfer = 0, fuel = 0;
    parts.forEach(p => {
        Object.entries(p.byDenom).forEach(([v, x]) => {
            byDenom[v].count += x.count;
            byDenom[v].total += x.total;
        });
        transfer += p.transfer;
        fuel += p.fuel;
    });
    const cashTotal = round2(Object.values(byDenom).reduce((s, x) => s + x.total, 0));
    return { denomList, byDenom, cashTotal, transfer: round2(transfer), fuel: round2(fuel) };
};

// ส่วนที่ 2 — ลำดับตามแผ่นบัญชี
// เก็บค้างจ่ายที่บัญชีบันทึกว่ารับเป็น "โอน" (debtPaidTransfer, Vector 2026-10-11) ไม่ใช่เงินสดในลิ้นชัก ⇒ หักออกอีกบรรทัด · ไม่ระบุช่องทาง/เงินสด = เหมือนเดิมทุกประการ
// รวมเงินสดทั้งหมด = ขาย − ค้างชำระ + เก็บค้างจ่าย − (ค้างจ่ายที่รับเป็นโอน) − คืนประกันกระสอบ (เราคืนเงินให้ลูกค้า = เงินออก) − โอน − น้ำมัน − อื่น ๆ
// ฝากกรุงไทย = รวมเงินสดทั้งหมด − เงินสดย่อย (ช่องเดียว ไม่แยกธนาคาร)
export const buildSummary = ({ salesTotal, debtNew, debtPaid, debtPaidTransfer = 0, bagDepositReturn, transfer, fuel, other, pettyCash }) => {
    const cashTotal = round2(num(salesTotal) - num(debtNew) + num(debtPaid) - num(debtPaidTransfer) - num(bagDepositReturn) - num(transfer) - num(fuel) - num(other));
    return { cashTotal, bankDeposit: round2(cashTotal - num(pettyCash)) };
};

// ส่วนที่ 4/5 — ต่อสาย: รวมใบรายงานหลายรอบของสายเดียวกันเป็นแถวเดียว (คงลำดับที่เจอก่อน)
export const groupByRoute = (reports, valueOf) => {
    const map = new Map();
    reports.forEach(r => {
        const key = r.sellerId || r.reportCode;
        if (!map.has(key)) map.set(key, { key, name: r.nameuser || r.username || r.sellerId || '-', value: 0 });
        map.get(key).value += valueOf(r);
    });
    return Array.from(map.values());
};

// ส่วนที่ 4 — เงินสดของรอบหน้าโรง (AGC-*) ที่ไม่มีใบส่งเงิน: ใช้ค่าที่ส่ง/กรอกแล้วก่อน (payments → เงินที่บัญชีกรอก) ไม่มีเลยค่อยใช้ "ยอดที่ต้องส่งตามระบบ" (expected.cashDue จาก seller — สูตรเดียวกับหน้า AgentSaleDataDetail)
// source: 'submitted' = มีเงินส่ง · 'system' = ตามระบบ (ยังไม่มีการกรอกเงินส่ง) · ไม่ใช่รอบหน้าโรง/ไม่มีข้อมูลระบบ = คืน paymentsCash ตามเดิม
export const resolveRouteCash = (paymentsCash, { factory = false, round = null } = {}) => {
    if (num(paymentsCash) > 0 || !factory || !round) return { value: round2(paymentsCash), source: 'submitted' };
    if (round.submitted) {
        const keyed = (round.cashDenominations || []).reduce((s, d) => s + num(d.denomination) * num(d.quantity ?? d.count), 0);
        if (keyed > 0) return { value: round2(keyed), source: 'submitted' };
    }
    const due = round.expected && round.expected.cashDue != null ? num(round.expected.cashDue) : null;
    if (due === null || round.submitted) return { value: round2(paymentsCash), source: 'submitted' };
    return { value: round2(due), source: 'system' };
};

// ป้ายเตือนส่วนต่าง — คู่ที่ควรเท่ากันแต่ไม่เท่า (ห้ามปรับให้เท่ากัน แค่โชว์)
export const buildWarnings = ({ salesQty, bagsQty, salesAmount, routeAmount, cashByDenom, cashByRoute, cashTotal }) => {
    const out = [];
    const check = (label, a, b, unit) => {
        const diff = round2(a - b);
        if (diff !== 0) out.push({ label, diff, unit });
    };
    check('จำนวนกระสอบ ส่วนที่ 1 ≠ ยอดกระสอบทุกสาย (รวม)', salesQty, bagsQty, 'กระสอบ');
    check('ยอดขาย ส่วนที่ 1 ≠ ยอดขายรวมของใบรายงานทุกสาย', salesAmount, routeAmount, 'บาท');
    check('ยอดเงินสดแยกแบงค์ ≠ รวมเงินสดทั้งหมด (ส่วนที่ 2)', cashByDenom, cashTotal, 'บาท');
    check('ยอดเงินสดทุกสาย ≠ รวมเงินสดทั้งหมด (ส่วนที่ 2)', cashByRoute, cashTotal, 'บาท');
    return out;
};

// หน้า "สรุปยอดขาย" — ยอดโอน/ค่าน้ำมัน: ค่าจากระบบ (IMD, aggregatePayments) vs ค่าที่คนกรอกมือไว้ (daily_report_adjustments)
// ค่ามือ ≠ 0 = มีคนกรอกไว้ ใช้ค่ามือ (โชว์ค่าระบบกำกับถ้าต่าง) · ค่ามือ 0/ไม่มี = ใช้ค่าระบบ · system = null = โหลด IMD ไม่ครบ ใช้ค่ามือล้วน
export const resolveAdjField = (manual, system) => {
    const m = Number(manual) || 0;
    const hasSystem = system !== null && system !== undefined;
    if (m !== 0) return { value: m, source: 'manual', system: hasSystem ? system : null, differs: hasSystem && round2(m) !== round2(system) };
    if (hasSystem) return { value: system, source: 'system', system, differs: false };
    return { value: 0, source: 'none', system: null, differs: false };
};
